import React, { useState, useCallback, useRef, useContext, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useMutation, useQuery, gql } from '@apollo/client'
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useViewport,
  Background,
  Controls,
  Handle,
  Position,
  getBezierPath,
  BaseEdge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { GET_PROJECTS_QUERY, GET_TASKS_QUERY } from '../query/gqlQueries.js'
import styles from './AddNoteDialog.module.scss'

import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UndoIcon from '@mui/icons-material/Undo'
import NearMeIcon from '@mui/icons-material/NearMe'
import EditIcon from '@mui/icons-material/Edit'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const ADD_NOTE_MUTATION = gql`
  mutation AddNote(
    $title: String!
    $mapData: String!
    $linkedProject: String
    $linkedTaskId: String
    $username: String!
  ) {
    addNote(input: {
      title: $title
      mapData: $mapData
      linkedProject: $linkedProject
      linkedTaskId: $linkedTaskId
      username: $username
    }) {
      identity
      elementId
      properties { title mapData linkedProject username dateAdded }
    }
  }
`

const UPDATE_NOTE_MUTATION = gql`
  mutation UpdateNote($identity: ID!, $input: NoteUpdateInput!) {
    updateNote(identity: $identity, input: $input) {
      identity
      elementId
      properties { title mapData linkedProject username dateAdded dateLastModified }
    }
  }
`

// ─── Constants ────────────────────────────────────────────────────────────────

const PEN_COLORS = ['#1a1a1a', '#2563eb', '#059669', 'var(--frost-wrong)', 'var(--frost-amber)', '#7c3aed']
const PEN_WIDTHS = [2, 4, 6, 10]
const ERASER_RADIUS = 18

// ─── Custom curved edge ──────────────────────────────────────────────────────

function MindMapEdge({ id, sourceX, sourceY, targetX, targetY, selected, markerEnd }) {
  const [edgePath] = getBezierPath({ sourceX, sourceY, targetX, targetY, curvature: 0.35 })
  return (
    <BaseEdge
      id={id}
      path={edgePath}
      markerEnd={markerEnd}
      style={{
        stroke: selected ? 'var(--frost-right)' : '#1a1a1a',
        strokeWidth: selected ? 2.5 : 2,
        transition: 'stroke 0.15s',
      }}
    />
  )
}

// ─── Custom text node ─────────────────────────────────────────────────────────

function MindMapNode({ id, data, selected }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(data.label || '')
  const inputRef = useRef(null)
  const { updateNodeData } = useReactFlow()

  useEffect(() => { setText(data.label || '') }, [data.label])
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  function commitEdit() {
    setEditing(false)
    updateNodeData(id, { label: text || 'Node' })
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit() }
    if (e.key === 'Escape') { setText(data.label); setEditing(false) }
  }

  const isRoot = data.root === true

  return (
    <div
      className={`${styles.mindNode} ${selected ? styles.selected : ''} ${isRoot ? styles.rootNode : ''}`}
      onDoubleClick={() => setEditing(true)}
      style={{ fontSize: isRoot ? 28 : 18 }}
    >
      <Handle type="target" position={Position.Top}    className={styles.handle} />
      <Handle type="target" position={Position.Left}   className={styles.handle} />
      <Handle type="source" position={Position.Bottom} className={styles.handle} id="bottom" />
      <Handle type="source" position={Position.Right}  className={styles.handle} id="right" />
      {editing ? (
        <textarea
          ref={inputRef}
          className={styles.nodeTextarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          rows={1}
          style={{ fontSize: isRoot ? 28 : 18 }}
        />
      ) : (
        <span className={styles.nodeLabel}>{data.label || 'Node'}</span>
      )}
    </div>
  )
}

const nodeTypes = { mindMap: MindMapNode }
const edgeTypes = { mindMap: MindMapEdge }
const defaultEdgeOptions = { type: 'mindMap', animated: false }

const DEFAULT_NODES = [
  { id: '1', type: 'mindMap', position: { x: 0, y: 0 }, data: { label: 'Main Idea', root: true } },
]
const DEFAULT_EDGES = []
let nodeCounter = 2

// ─── Drawing layer ────────────────────────────────────────────────────────────

function StrokeRender({ stroke }) {
  if (!stroke || stroke.points.length === 0) return null
  if (stroke.points.length === 1) {
    const p = stroke.points[0]
    return <circle cx={p.x} cy={p.y} r={(stroke.width * (p.pressure ?? 0.5) + 0.5) / 2} fill={stroke.color} />
  }
  const segs = []
  for (let i = 1; i < stroke.points.length; i++) {
    const p1 = stroke.points[i - 1]
    const p2 = stroke.points[i]
    const avg = ((p1.pressure ?? 0.5) + (p2.pressure ?? 0.5)) / 2
    const w = stroke.width * (0.35 + avg * 1.3) + 0.3
    segs.push(
      <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
        stroke={stroke.color} strokeWidth={w} strokeLinecap="round" />
    )
  }
  return <g>{segs}</g>
}

function DrawingLayer({ strokes, currentStroke }) {
  const { x, y, zoom } = useViewport()
  return (
    <svg className={styles.drawingSvg}>
      <g transform={`translate(${x} ${y}) scale(${zoom})`}>
        {strokes.map((s) => <StrokeRender key={s.id} stroke={s} />)}
        {currentStroke && <StrokeRender stroke={currentStroke} />}
      </g>
    </svg>
  )
}

// ─── Inner canvas ─────────────────────────────────────────────────────────────

function MindMapCanvas({ onSave, onClose, title, setTitle, linkedProject, setLinkedProject,
  linkedTaskId, setLinkedTaskId, isSaving, isEditMode, projectsData, tasksData,
  initialNodes, initialEdges, initialStrokes }) {

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes || DEFAULT_NODES)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || DEFAULT_EDGES)
  const [selectedNodes, setSelectedNodes] = useState([])
  const [selectedEdges, setSelectedEdges] = useState([])
  const { screenToFlowPosition } = useReactFlow()

  // Drawing state
  const [tool, setTool] = useState('select')
  const [penColor, setPenColor] = useState(PEN_COLORS[0])
  const [penWidth, setPenWidth] = useState(4)
  const [strokes, setStrokes] = useState(initialStrokes || [])
  const [currentStroke, setCurrentStroke] = useState(null)
  const isDrawingRef = useRef(false)

  // Sync initial data when opening existing note
  useEffect(() => {
    if (initialNodes) setNodes(initialNodes)
    if (initialEdges) setEdges(initialEdges)
    if (initialStrokes) setStrokes(initialStrokes)
  }, [initialNodes, initialEdges, initialStrokes])

  // Undo history
  const historyRef = useRef([])
  function snapshot() {
    historyRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
      strokes: JSON.parse(JSON.stringify(strokes)),
    })
    if (historyRef.current.length > 50) historyRef.current.shift()
  }
  function undo() {
    const prev = historyRef.current.pop()
    if (!prev) return
    setNodes(prev.nodes)
    setEdges(prev.edges)
    setStrokes(prev.strokes)
  }

  // ─── Mind map handlers ───────────────────────────────────────────────────

  const onConnect = useCallback((params) => {
    snapshot()
    setEdges((eds) => addEdge({ ...params, type: 'mindMap' }, eds))
  }, [nodes, edges, strokes])

  function onCanvasDoubleClick(e) {
    if (tool !== 'select') return
    if (e.target.closest('.react-flow__node')) return
    snapshot()
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    const id = String(nodeCounter++)
    setNodes((nds) => [...nds, { id, type: 'mindMap', position, data: { label: 'Node' } }])
  }

  function addNode() {
    snapshot()
    const id = String(nodeCounter++)
    setNodes((nds) => [
      ...nds,
      { id, type: 'mindMap', position: { x: (Math.random() - 0.5) * 300, y: (Math.random() - 0.5) * 300 }, data: { label: 'New Node' } },
    ])
  }

  function deleteSelected() {
    if (selectedNodes.length === 0 && selectedEdges.length === 0) return
    snapshot()
    const ids = new Set(selectedNodes.map((n) => n.id))
    setNodes((nds) => nds.filter((n) => !ids.has(n.id)))
    const eids = new Set(selectedEdges.map((e) => e.id))
    setEdges((eds) => eds.filter((e) => !eids.has(e.id) && !ids.has(e.source) && !ids.has(e.target)))
  }

  function onSelectionChange({ nodes: sn, edges: se }) {
    setSelectedNodes(sn || [])
    setSelectedEdges(se || [])
  }

  // ─── Drawing handlers ────────────────────────────────────────────────────

  function eraseAt(flowPos) {
    setStrokes((prev) =>
      prev.filter((s) => !s.points.some((p) => {
        const dx = p.x - flowPos.x
        const dy = p.y - flowPos.y
        return dx * dx + dy * dy < ERASER_RADIUS * ERASER_RADIUS
      }))
    )
  }

  function onDrawPointerDown(e) {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    isDrawingRef.current = true
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    if (tool === 'pen') {
      snapshot()
      setCurrentStroke({
        id: `s_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
        color: penColor,
        width: penWidth,
        points: [{ x: flowPos.x, y: flowPos.y, pressure: e.pressure || 0.5 }],
      })
    } else if (tool === 'eraser') {
      snapshot()
      eraseAt(flowPos)
    }
  }

  function onDrawPointerMove(e) {
    if (!isDrawingRef.current) return
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    if (tool === 'pen') {
      setCurrentStroke((prev) => {
        if (!prev) return prev
        const last = prev.points[prev.points.length - 1]
        if (last && Math.abs(last.x - flowPos.x) < 0.5 && Math.abs(last.y - flowPos.y) < 0.5) return prev
        return { ...prev, points: [...prev.points, { x: flowPos.x, y: flowPos.y, pressure: e.pressure || 0.5 }] }
      })
    } else if (tool === 'eraser') {
      eraseAt(flowPos)
    }
  }

  function onDrawPointerUp() {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    if (tool === 'pen') {
      setCurrentStroke((stroke) => {
        if (stroke && stroke.points.length > 0) setStrokes((prev) => [...prev, stroke])
        return null
      })
    }
  }

  const isPenActive = tool === 'pen' || tool === 'eraser'

  return (
    <div className={styles.dialog}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.topLeft}>
          <span className={styles.topLabel}>{isEditMode ? 'Edit Note' : 'New Note'}</span>
          <input
            className={styles.titleInput}
            placeholder="Note title…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className={styles.topRight}>
          <select className={styles.select} value={linkedProject} onChange={(e) => setLinkedProject(e.target.value)}>
            <option value="">No project</option>
            {projectsData?.getProjects?.map((p) => (
              <option key={p.identity} value={p.properties.title}>{p.properties.title}</option>
            ))}
          </select>
          <select className={styles.select} value={linkedTaskId} onChange={(e) => setLinkedTaskId(e.target.value)}>
            <option value="">No task</option>
            {tasksData?.getTasks?.map((t) => (
              <option key={t.identity} value={t.identity}>{t.properties.title}</option>
            ))}
          </select>
          <button
            className={styles.saveBtn}
            onClick={() => onSave(JSON.stringify({ nodes, edges, strokes }))}
            disabled={isSaving || !title.trim()}>
            {isSaving ? 'Saving…' : isEditMode ? 'Update' : 'Save'}
          </button>
          <button className={styles.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Floating toolbar */}
      <div className={styles.floatingBar}>
        <div className={styles.toolGroup}>
          <button className={`${styles.toolBtn} ${tool === 'select' ? styles.toolBtnActive : ''}`} onClick={() => setTool('select')} title="Select / Move">
            <NearMeIcon fontSize="small" />
          </button>
          <button className={`${styles.toolBtn} ${tool === 'pen' ? styles.toolBtnActive : ''}`} onClick={() => setTool('pen')} title="Pen">
            <EditIcon fontSize="small" />
          </button>
          <button className={`${styles.toolBtn} ${tool === 'eraser' ? styles.toolBtnActive : ''}`} onClick={() => setTool('eraser')} title="Eraser">
            <AutoFixHighIcon fontSize="small" />
          </button>
        </div>

        {tool === 'pen' && (
          <>
            <div className={styles.floatDivider} />
            <div className={styles.toolGroup}>
              {PEN_COLORS.map((c) => (
                <button key={c} className={`${styles.colorSwatch} ${penColor === c ? styles.colorSwatchActive : ''}`}
                  style={{ background: c }} onClick={() => setPenColor(c)} />
              ))}
            </div>
            <div className={styles.floatDivider} />
            <div className={styles.toolGroup}>
              {PEN_WIDTHS.map((w) => (
                <button key={w} className={`${styles.widthBtn} ${penWidth === w ? styles.widthBtnActive : ''}`} onClick={() => setPenWidth(w)}>
                  <span className={styles.widthDot} style={{ width: w + 4, height: w + 4, background: penColor }} />
                </button>
              ))}
            </div>
          </>
        )}

        {tool === 'select' && (
          <>
            <div className={styles.floatDivider} />
            <button className={styles.actionBtn} onClick={addNode}><AddIcon fontSize="small" /><span>Add Node</span></button>
            <button
              className={`${styles.actionBtn} ${(selectedNodes.length || selectedEdges.length) ? styles.actionBtnDanger : ''}`}
              onClick={deleteSelected}
              disabled={selectedNodes.length === 0 && selectedEdges.length === 0}>
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </>
        )}

        <div className={styles.floatDivider} />
        <button className={styles.actionBtn} onClick={undo}><UndoIcon fontSize="small" /></button>
      </div>

      {/* Canvas */}
      <div className={styles.canvasArea} onDoubleClick={onCanvasDoubleClick}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          fitViewOptions={{ padding: 0.5 }}
          minZoom={0.2}
          maxZoom={3}
          deleteKeyCode={isPenActive ? null : ['Backspace', 'Delete']}
          panOnScroll
          zoomOnDoubleClick={false}
          nodesDraggable={!isPenActive}
          nodesConnectable={!isPenActive}
          elementsSelectable={!isPenActive}
          panOnDrag={!isPenActive}
          style={{ background: '#ffffff', width: '100%', height: '100%' }}
        >
          <Background color="#ebebeb" gap={28} size={1} variant="dots" />
          <Controls showInteractive={false} />
        </ReactFlow>

        <DrawingLayer strokes={strokes} currentStroke={currentStroke} />

        {isPenActive && (
          <div
            className={styles.penOverlay}
            style={{ cursor: tool === 'eraser' ? 'cell' : 'crosshair' }}
            onPointerDown={onDrawPointerDown}
            onPointerMove={onDrawPointerMove}
            onPointerUp={onDrawPointerUp}
            onPointerCancel={onDrawPointerUp}
          />
        )}
      </div>
    </div>
  )
}

// ─── Outer wrapper ────────────────────────────────────────────────────────────

function AddNoteDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [title, setTitle] = useState('')
  const [linkedProject, setLinkedProject] = useState('')
  const [linkedTaskId, setLinkedTaskId] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Existing note being edited
  const [editingNote, setEditingNote] = useState(null)
  const [initialNodes, setInitialNodes] = useState(null)
  const [initialEdges, setInitialEdges] = useState(null)
  const [initialStrokes, setInitialStrokes] = useState(null)

  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const { data: tasksData }    = useQuery(GET_TASKS_QUERY,    { variables: { username }, skip: !username })
  const [addNote]    = useMutation(ADD_NOTE_MUTATION)
  const [updateNote] = useMutation(UPDATE_NOTE_MUTATION)

  const isOpen = dialogStates['dialogAddNote']

  // Listen for openNote events fired by NotesListDialog
  useEffect(() => {
    function handleOpenNote(e) {
      const note = e.detail
      if (!note) return
      const props = note.properties || {}
      setEditingNote(note)
      setTitle(props.title || '')
      setLinkedProject(props.linkedProject || '')
      setLinkedTaskId(props.linkedTaskId || '')
      // Parse the saved map data
      try {
        const parsed = JSON.parse(props.mapData || '{}')
        setInitialNodes(parsed.nodes || DEFAULT_NODES)
        setInitialEdges(parsed.edges || DEFAULT_EDGES)
        setInitialStrokes(parsed.strokes || [])
      } catch {
        setInitialNodes(DEFAULT_NODES)
        setInitialEdges(DEFAULT_EDGES)
        setInitialStrokes([])
      }
    }
    window.addEventListener('openNote', handleOpenNote)
    return () => window.removeEventListener('openNote', handleOpenNote)
  }, [])

  async function handleSave(mapData) {
    setIsSaving(true)
    try {
      if (editingNote) {
        // Update existing note
        await updateNote({
          variables: {
            identity: editingNote.identity,
            input: {
              title: title.trim(),
              mapData,
              linkedProject: linkedProject || null,
              linkedTaskId: linkedTaskId || null,
            },
          },
        })
      } else {
        // Create new note
        await addNote({
          variables: { title: title.trim(), mapData, linkedProject: linkedProject || null, linkedTaskId: linkedTaskId || null, username },
        })
      }
      handleClose()
    } catch (err) {
      console.error('Error saving note:', err)
    } finally {
      setIsSaving(false)
    }
  }

  function handleClose() {
    setTitle('')
    setLinkedProject('')
    setLinkedTaskId('')
    setEditingNote(null)
    setInitialNodes(null)
    setInitialEdges(null)
    setInitialStrokes(null)
    nodeCounter = 2
    closeDialog('dialogAddNote')
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <ReactFlowProvider>
        <MindMapCanvas
          key={editingNote?.identity || 'new'}
          onSave={handleSave}
          onClose={handleClose}
          title={title} setTitle={setTitle}
          linkedProject={linkedProject} setLinkedProject={setLinkedProject}
          linkedTaskId={linkedTaskId}   setLinkedTaskId={setLinkedTaskId}
          isSaving={isSaving}
          isEditMode={!!editingNote}
          initialNodes={initialNodes}
          initialEdges={initialEdges}
          initialStrokes={initialStrokes}
          projectsData={projectsData}
          tasksData={tasksData}
        />
      </ReactFlowProvider>
    </div>,
    document.body
  )
}

export default AddNoteDialog
